"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Settings, Play, FileText, Download, Search, Upload, ArrowLeft, Loader2, ChevronDown, Plus, X, Table, MessageSquare, Save } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface IncrementAnalysisProps {
  onBack?: () => void;
}

export default function IncrementAnalysis({ onBack }: IncrementAnalysisProps) {
  const { isArchivalMode } = useArchivalMode();
  // SharePoint search and file management
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  
  // File selections
  const [ctcReport, setCtcReport] = useState<string>("");
  const [ctcPrevReport, setCtcPrevReport] = useState<string>("");

  const [ctcColumns, setCtcColumns] = useState<string[]>([]);
  const [ctcPrevColumns, setCtcPrevColumns] = useState<string[]>([]);
  const [isLoadingCtcColumns, setIsLoadingCtcColumns] = useState(false);
  const [isLoadingCtcPrevColumns, setIsLoadingCtcPrevColumns] = useState(false);

  // Individual column selections
  const [empNo, setEmpNo] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [doj, setDoj] = useState<string>("");
  const [designation, setDesignation] = useState<string>("");
  const [empNoPrev, setEmpNoPrev] = useState<string>("");
  const [columnsToSum, setColumnsToSum] = useState<string[]>([]);

  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileUrl, setFileUrl] = useState<string>("");

  // Load table data
  const [tableData, setTableData] = useState<{
    "As per Increment analysis": number;
    "As per CTC Report": number;
    "User reconciliations": any[];
    "Difference": number;
    "Average Increment %": number;
    "Net Difference": number;
  } | null>(null);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [tableFileNotFound, setTableFileNotFound] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [userReconciliations, setUserReconciliations] = useState<Array<{ amount: number; reason: string }>>([]);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `INCREMENT_ANALYSIS_${normalizedSection}`;
  };

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_EBE");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_EBE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_EBE_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "INCREMENT-ANALYSIS-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger search for files and load table when component mounts (when user clicks on Increment Analysis panel)
  useEffect(() => {
    searchForFiles();
    loadTableData();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Employee Benefits Expense", "Increment Analysis");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              // Get FY_YEAR from database dynamically
              const fyYear = getFY_YEAR();
              if (!fyYear) {
                throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
              }

              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: String(fyYear),
                sectionList: ["Execution_EBE"],
              });

              if (result.success) {
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadCtcReportColumns = async () => {
    if (!ctcReport) return;
    setIsLoadingCtcColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const fileName = ctcReport.includes(' (') ? ctcReport.split(' (')[0] : ctcReport;
        const result = await window.payroll.loadExcelColumns(fileName);
        if (result.ok && result.columns) {
          setCtcColumns(result.columns);
          setEmpNo("");
          setName("");
          setDoj("");
          setDesignation("");
          setColumnsToSum([]);
          // Save file name to file usage reference
          await updateProcedureFileUsageForWorkspace("Employee Benefits Expense", "Increment Analysis", [{ "CTC Report": fileName }]);
          await loadFileUsageReference();
        }
      }
    } finally {
      setIsLoadingCtcColumns(false);
    }
  };

  const loadCtcPrevReportColumns = async () => {
    if (!ctcPrevReport) return;
    setIsLoadingCtcPrevColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const fileName = ctcPrevReport.includes(' (') ? ctcPrevReport.split(' (')[0] : ctcPrevReport;
        const result = await window.payroll.loadExcelColumns(fileName);
        if (result.ok && result.columns) {
          setCtcPrevColumns(result.columns);
          setEmpNoPrev("");
          // Save file name to file usage reference
          await updateProcedureFileUsageForWorkspace("Employee Benefits Expense", "Increment Analysis", [{ "CTC Previous Year Report": fileName }]);
          await loadFileUsageReference();
        }
      }
    } finally {
      setIsLoadingCtcPrevColumns(false);
    }
  };

  const toggleFrom = (setter: React.Dispatch<React.SetStateAction<string[]>>, list: string[], value: string) => {
    setter(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  const loadTableData = async () => {
    setIsLoadingTable(true);
    setTableFileNotFound(false);
    try {
      // Call IPC handler to download JSON from SharePoint
      if (!window.sharePointAPI?.downloadIncrementAnalysisJson) {
        throw new Error("SharePoint API method 'downloadIncrementAnalysisJson' is not available. Please restart the application.");
      }
      const result = await window.sharePointAPI.downloadIncrementAnalysisJson();
      
      if (result?.success && result?.data) {
        const data = result.data;
        // Check if the data is the default empty data (file not found)
        // This happens when the backend returns all zeros/empty values for a 404 response
        const isDefaultEmptyData = 
          data["As per Increment analysis"] === 0 &&
          data["As per CTC Report"] === 0 &&
          (!Array.isArray(data["User reconciliations"]) || data["User reconciliations"].length === 0) &&
          data["Difference"] === 0 &&
          data["Average Increment %"] === 0 &&
          data["Net Difference"] === 0;
        
        if (isDefaultEmptyData) {
          // File not found - show message instead of table
          setTableFileNotFound(true);
          setTableData(null);
          setUserReconciliations([]);
        } else {
          // Valid data exists - show table
          setTableFileNotFound(false);
          setTableData(data);
          // Initialize user reconciliations from loaded data or empty array
          if (Array.isArray(data["User reconciliations"]) && data["User reconciliations"].length > 0) {
            // Check if it's an array of arrays: [[amount, reason], ...]
            if (Array.isArray(data["User reconciliations"][0])) {
              // Convert [[amount, reason], ...] to [{ amount, reason }, ...]
              const parsedReconciliations = data["User reconciliations"].map((item: any[]) => {
                return {
                  amount: typeof item[0] === 'number' ? item[0] : 0,
                  reason: typeof item[1] === 'string' ? item[1] : ""
                };
              });
              setUserReconciliations(parsedReconciliations);
            } else if (data["User reconciliations"][0]?.amount !== undefined) {
              // If it's already an array of objects with amount and reason, use it directly
              setUserReconciliations(data["User reconciliations"]);
            } else {
              // Otherwise initialize as empty
              setUserReconciliations([]);
            }
          } else {
            setUserReconciliations([]);
          }
        }
      } else {
        // result.success is false - file not found or other error
        setTableFileNotFound(true);
        setTableData(null);
        setUserReconciliations([]);
      }
    } catch (error) {
      console.error("Error loading table data:", error);
      // On error, treat as file not found and show message
      setTableFileNotFound(true);
      setTableData(null);
      setUserReconciliations([]);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const addUserReconciliation = () => {
    setUserReconciliations([...userReconciliations, { amount: 0, reason: "" }]);
  };

  const removeUserReconciliation = (index: number) => {
    setUserReconciliations(userReconciliations.filter((_, i) => i !== index));
  };

  const updateUserReconciliation = (index: number, field: "amount" | "reason", value: number | string) => {
    const updated = [...userReconciliations];
    updated[index] = { ...updated[index], [field]: value };
    setUserReconciliations(updated);
  };

  const reuploadData = async () => {
    if (!tableData) {
      alert("Please load table data first");
      return;
    }

    setIsUploading(true);
    try {
      // Calculate sum of user reconciliations amounts
      const userReconciliationsSum = userReconciliations.reduce((sum, rec) => sum + (rec.amount || 0), 0);

      // Format user reconciliations as array of arrays: [[amount, reason], [amount, reason]]
      const userReconciliationsFormatted = userReconciliations.map(rec => [rec.amount || 0, rec.reason || ""]);

      // Prepare the JSON data with current UI state
      // Difference = As per Increment analysis - As per CTC Report
      // Net Difference = Difference - sum of user reconciliations
      const calculatedDifference = tableData["As per Increment analysis"] - tableData["As per CTC Report"];
      const jsonData = {
        "As per Increment analysis": tableData["As per Increment analysis"],
        "As per CTC Report": tableData["As per CTC Report"],
        "User reconciliations": userReconciliationsFormatted,
        "Difference": calculatedDifference,
        "Average Increment %": tableData["Average Increment %"],
        "Net Difference": calculatedDifference - userReconciliationsSum
      };

      // Convert to JSON string
      const jsonString = JSON.stringify(jsonData, null, 4);
      
      // Convert to base64 for upload (proper UTF-8 encoding)
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      // Upload using SharePoint API
      if (window.sharePointAPI?.uploadFile) {
        // Get FY_YEAR from database dynamically
        const fyYear = getFY_YEAR();
        if (!fyYear) {
          throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
        }

        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: "Execution_EBE_IncrementAnalysis.json",
          referenceValue: "",
          folderName: "juggernaut",
          fyYear: String(fyYear),
          sectionList: ["Execution_EBE_IncrementAnalysis"]
        });

        if (result.success) {
          alert("Successfully uploaded increment analysis data to SharePoint!");
          // Reload table data after successful upload
          await loadTableData();
        } else {
          throw new Error(result.error || "Upload failed");
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error uploading data:", error);
      alert("Failed to upload data: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  const canRun = () => {
    return (
      ctcReport &&
      ctcPrevReport &&
      ctcColumns.length > 0 &&
      ctcPrevColumns.length > 0 &&
      empNo &&
      name &&
      doj &&
      designation &&
      empNoPrev &&
      columnsToSum.length > 0
    );
  };

  const runIncrementAnalysis = async () => {
    if (!canRun()) return;
    
    // Save column selections to file usage reference
    const entries: FileUsageEntry[] = [];
    if (ctcReport) {
      const fileName = ctcReport.includes(' (') ? ctcReport.split(' (')[0] : ctcReport;
      entries.push({ "CTC Report": fileName });
    }
    if (ctcPrevReport) {
      const fileName = ctcPrevReport.includes(' (') ? ctcPrevReport.split(' (')[0] : ctcPrevReport;
      entries.push({ "CTC Previous Year Report": fileName });
    }
    // Save Configure columns mapping
    const columnMapping: Record<string, string> = {};
    if (empNo) columnMapping["Employee No"] = empNo;
    if (name) columnMapping["Name"] = name;
    if (doj) columnMapping["Date of Joining"] = doj;
    if (designation) columnMapping["Designation"] = designation;
    if (empNoPrev) columnMapping["Employee No Prev"] = empNoPrev;
    if (columnsToSum.length > 0) columnMapping["Columns to Sum"] = columnsToSum.join(", ");
    if (Object.keys(columnMapping).length > 0) {
      entries.push({ "Configure columns": JSON.stringify(columnMapping) });
    }
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Employee Benefits Expense", "Increment Analysis", entries);
      await loadFileUsageReference();
    }
    
    setProcessingStatus("running");
    setFileUrl("");

    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_increment_analysis_sharepoint", {
          inputFiles: [],
          options: {
            cy_file: ctcReport.includes(' (') ? ctcReport.split(' (')[0] : ctcReport,
            py_file: ctcPrevReport.includes(' (') ? ctcPrevReport.split(' (')[0] : ctcPrevReport,
            incr_columns: [empNo, name, doj, designation],
            py_empno_col: empNoPrev,
            cols_to_sum: columnsToSum,
            reconcile_input: [],
          },
        });

        if (result.ok) {
          setProcessingStatus("completed");
          try {
            if (result.stdout) {
              const lines = String(result.stdout).split('\n');
              const jsonLine = lines.find((l: string) => l.trim().startsWith('{') && l.trim().endsWith('}'));
              if (jsonLine) {
                const parsed = JSON.parse(jsonLine);
                if (parsed.excel_url) setFileUrl(parsed.excel_url);
              }
            }
          } catch {}
          await loadTableData();
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch (error) {
      setProcessingStatus("error");
    }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "EBE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "INCREMENT-ANALYSIS-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Increment Analysis</h2>
          <p className="text-gray-400">Compare current vs previous year CTC to compute increments</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      {/* Step 1: Search for Existing Files */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
            {fileSearchStatus === "idle" && (
              <span className="text-gray-400">Searching for files...</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <Label className="text-white">CTC Report (Current Year)</Label>
                  <Select value={ctcReport} onValueChange={setCtcReport}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                      {foundFiles.map((f, i) => (<SelectItem key={i} value={f}>{f}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["CTC Report"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["CTC Report"])?.["CTC Report"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-white">CTC Previous Year Report</Label>
                  <Select value={ctcPrevReport} onValueChange={setCtcPrevReport}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                      {foundFiles.map((f, i) => (<SelectItem key={i} value={f}>{f}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["CTC Previous Year Report"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["CTC Previous Year Report"])?.["CTC Previous Year Report"]}
                    </p>
                  )}
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={loadCtcReportColumns} disabled={!ctcReport || isLoadingCtcColumns} className="ml-auto">
                    {isLoadingCtcColumns ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Settings className="h-4 w-4" />
                    )}
                    {isLoadingCtcColumns ? 'Loading...' : 'Load CTC Columns'}
                  </Button>
                  <Button onClick={loadCtcPrevReportColumns} disabled={!ctcPrevReport || isLoadingCtcPrevColumns} className="ml-auto">
                    {isLoadingCtcPrevColumns ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Settings className="h-4 w-4" />
                    )}
                    {isLoadingCtcPrevColumns ? 'Loading...' : 'Load Prev Year Columns'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={isArchivalMode || uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Step 2: Configure Columns */}
      {(ctcColumns.length > 0 || ctcPrevColumns.length > 0) && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-white">
                <Settings className="h-5 w-5" />
                Step 2: Configure Columns
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Configure Columns"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Increment Columns (5 dropdowns) */}
            <div className="space-y-4">
              <Label className="text-white">Map Columns from CTC Report</Label>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-white">Employee Number</Label>
                  <Select value={empNo} onValueChange={setEmpNo} disabled={ctcColumns.length === 0}>
                    <SelectTrigger>
                      <SelectValue placeholder={ctcColumns.length === 0 ? "Load columns first" : "Select column"} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {ctcColumns.map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Configure columns"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Configure columns"])?.["Configure columns"] || "{}");
                      const lastUsed = mapping["Employee No"];
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Name</Label>
                  <Select value={name} onValueChange={setName} disabled={ctcColumns.length === 0}>
                    <SelectTrigger>
                      <SelectValue placeholder={ctcColumns.length === 0 ? "Load columns first" : "Select column"} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {ctcColumns.map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Configure columns"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Configure columns"])?.["Configure columns"] || "{}");
                      const lastUsed = mapping["Name"];
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Date of Joining</Label>
                  <Select value={doj} onValueChange={setDoj} disabled={ctcColumns.length === 0}>
                    <SelectTrigger>
                      <SelectValue placeholder={ctcColumns.length === 0 ? "Load columns first" : "Select column"} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {ctcColumns.map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Configure columns"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Configure columns"])?.["Configure columns"] || "{}");
                      const lastUsed = mapping["Date of Joining"];
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Designation</Label>
                  <Select value={designation} onValueChange={setDesignation} disabled={ctcColumns.length === 0}>
                    <SelectTrigger>
                      <SelectValue placeholder={ctcColumns.length === 0 ? "Load columns first" : "Select column"} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {ctcColumns.map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Configure columns"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Configure columns"])?.["Configure columns"] || "{}");
                      const lastUsed = mapping["Designation"];
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Employee Number of Previous Year CTC</Label>
                  <Select value={empNoPrev} onValueChange={setEmpNoPrev} disabled={ctcPrevColumns.length === 0}>
                    <SelectTrigger>
                      <SelectValue placeholder={ctcPrevColumns.length === 0 ? "Load prev year columns first" : "Select column"} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {ctcPrevColumns.map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Configure columns"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Configure columns"])?.["Configure columns"] || "{}");
                      const lastUsed = mapping["Employee No Prev"];
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
              </div>
              {(!empNo || !name || !doj || !designation || !empNoPrev) && (
                <p className="text-xs text-yellow-400">Please select all 5 columns.</p>
              )}
            </div>

            {/* Columns to Sum - Multi-select dropdown */}
            <div className="space-y-4">
              <Label className="text-white">Columns to Sum</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-black/60"
                    disabled={ctcColumns.length === 0}
                  >
                    <span className="truncate">
                      {columnsToSum.length === 0
                        ? ctcColumns.length === 0
                          ? "Load columns first"
                          : "Select columns to sum"
                        : `${columnsToSum.length} column${columnsToSum.length > 1 ? "s" : ""} selected`}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="min-w-[300px] border-white/10 bg-black/90 text-white p-2 max-h-64 overflow-y-auto" align="start">
                  <div className="space-y-2">
                    {ctcColumns.map((columnName) => (
                      <div key={columnName} className="flex items-center space-x-2">
                        <Checkbox
                          id={`sum-${columnName}`}
                          checked={columnsToSum.includes(columnName)}
                          onCheckedChange={() => toggleFrom(setColumnsToSum, columnsToSum, columnName)}
                        />
                        <Label htmlFor={`sum-${columnName}`} className="flex items-center gap-2 text-white cursor-pointer">
                          {columnName}
                        </Label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {columnsToSum.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {columnsToSum.map((col) => (
                    <span
                      key={col}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-sm"
                    >
                      {col}
                      <button
                        onClick={() => setColumnsToSum(columnsToSum.filter((c) => c !== col))}
                        className="hover:text-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isArchivalMode}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {columnsToSum.length === 0 && ctcColumns.length > 0 && (
                <p className="text-xs text-yellow-400">Select at least one column to sum.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Execute */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5" />
              Execute Increment Analysis
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute Increment Analysis"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Run increment analysis on selected CTC files</p>
              {!canRun() && (
                <p className="mt-1 text-xs text-red-400">Select both files, load columns, choose all 5 mapping columns and at least one sum column.</p>
              )}
            </div>
            <Button
              onClick={runIncrementAnalysis}
              disabled={isArchivalMode || !canRun() || processingStatus === "running"}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {processingStatus === "running" ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Increment Analysis
                </>
              )}
            </Button>
          </div>

          {processingStatus === "completed" && (
            <div className="rounded border border-green-500/20 bg-green-500/10 p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-green-500" />
                <div>
                  <h4 className="font-medium text-green-300">Increment Analysis Completed</h4>
                  <p className="text-sm text-green-200">Updated Excel uploaded to SharePoint.</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" className="border-green-500/30 text-green-300" onClick={() => { if (fileUrl) window.open(fileUrl, "_blank"); }} disabled={!fileUrl}>
                  <Download className="mr-2 h-4 w-4" />
                  Open Excel
                </Button>
              </div>
            </div>
          )}

          {processingStatus === "error" && (
            <div className="rounded border border-red-500/20 bg-red-500/10 p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-red-500" />
                <div>
                  <h4 className="font-medium text-red-300">Increment Analysis Failed</h4>
                  <p className="text-sm text-red-200">Please check inputs and try again.</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Load Table */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Table className="h-5 w-5" />
              Increment Analysis Summary
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Increment Analysis Summary"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingTable && (
            <div className="flex items-center gap-2 text-blue-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading table data...</span>
            </div>
          )}

          {tableFileNotFound && !isLoadingTable && (
            <div className="flex items-center justify-center p-8 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <p className="text-yellow-400 text-center">
                Increment Analysis has not been run for a summary display.
              </p>
            </div>
          )}

          {tableData && !tableFileNotFound && (
            <div className="space-y-6">
              {/* Display Table Data */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-white/10">
                  <thead>
                    <tr className="bg-black/60">
                      <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                      <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Number of Employees</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">As per Increment analysis</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white">{tableData["As per Increment analysis"]}</td>
                    </tr>
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">As per CTC Report</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white">{tableData["As per CTC Report"]}</td>
                    </tr>
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                        {tableData["As per Increment analysis"] - tableData["As per CTC Report"]}
                      </td>
                    </tr>
                    {userReconciliations.map((rec, index) => (
                      <tr key={`reconciliation-${index}`} className="hover:bg-white/5">
                        <td className="border border-white/10 px-4 py-2 text-white">{rec.reason || `User Reconciliation ${index + 1}`}</td>
                        <td className="border border-white/10 px-4 py-2 text-right text-white">{rec.amount || 0}</td>
                      </tr>
                    ))}
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                        {(tableData["As per Increment analysis"] - tableData["As per CTC Report"]) - userReconciliations.reduce((sum, rec) => sum + (rec.amount || 0), 0)}
                      </td>
                    </tr>
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">Average Increment %</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white">{tableData["Average Increment %"]}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* User Reconciliations */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-white text-lg font-semibold">User Reconciliations</Label>
                  <div className="flex gap-2">
                    <Button
                      onClick={addUserReconciliation}
                      size="sm"
                      variant="outline"
                      className="border-white/10 text-white hover:bg-white/10"
                      disabled={isArchivalMode}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Row
                    </Button>
                    <Button
                      onClick={reuploadData}
                      disabled={isArchivalMode || isUploading || !tableData}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Reupload
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {userReconciliations.length === 0 ? (
                  <p className="text-sm text-gray-400">No user reconciliations added. Click "Add Row" to add one.</p>
                ) : (
                  <div className="space-y-3">
                    {userReconciliations.map((rec, index) => (
                      <div key={index} className="flex gap-3 items-start p-3 border border-white/10 rounded-lg bg-black/20">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-white text-sm">Reason</Label>
                            <Input
                              type="text"
                              value={rec.reason}
                              onChange={(e) => updateUserReconciliation(index, "reason", e.target.value)}
                              className="border-white/10 bg-black/40 text-white"
                              placeholder="Enter reason"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-white text-sm">Number of Employees</Label>
                            <Input
                              type="number"
                              value={rec.amount}
                              onChange={(e) => updateUserReconciliation(index, "amount", parseFloat(e.target.value) || 0)}
                              className="border-white/10 bg-black/40 text-white"
                              placeholder="Enter number of employees"
                            />
                          </div>
                        </div>
                        <Button
                          onClick={() => removeUserReconciliation(index)}
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="payroll"
            itemId="INCREMENT-ANALYSIS-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Step 2: Configure Columns")]: "Step 2: Configure Columns",
              [getSectionQuestionId("Execute Increment Analysis")]: "Execute Increment Analysis",
              [getSectionQuestionId("Increment Analysis Summary")]: "Increment Analysis Summary",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
